/**
 * FP3-031 — Shared Student Collaboration Layer (Team tab).
 *
 * Lives at /students/{id}?tab=team. Two stacked panels:
 *   1. Team panel — primary, assistant, and coverage providers with
 *      clear discipline / supervision / coverage context.
 *   2. Collaboration notes — student-scoped, team-attributed
 *      timeline with a calm composer at the top.
 *
 * Privacy boundary: this tab NEVER reads from `/messages` or
 * `/messages/provider`. Those are private DMs. Distinct surface,
 * distinct collection (`student_collaboration_notes`).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Users,
  ChatCircleDots,
  Clock,
  PencilSimple,
  TrashSimple,
  Check,
  X,
  Warning,
  CaretRight,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/common/ConfirmDialog";

const CATEGORY_OPTIONS = [
  { value: "general_update", label: "General update" },
  { value: "schedule", label: "Schedule" },
  { value: "goal_question", label: "Goal question" },
  { value: "progress_concern", label: "Progress concern" },
  { value: "documentation", label: "Documentation" },
  { value: "meeting_prep", label: "Meeting prep" },
  { value: "coverage", label: "Coverage" },
  { value: "other", label: "Other" },
];

const ROLE_BADGE = {
  primary: { label: "Primary", className: "bg-[#E8F0EB] text-[#1E3F33]" },
  assistant: { label: "Assistant", className: "bg-[#F0EDE3] text-[#6B5A2C]" },
  coverage: { label: "Coverage", className: "bg-[#FAF1DD] text-[#7A5B1F]" },
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function formatTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function StudentTeamTab({ studentId }) {
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [teamLoading, setTeamLoading] = useState(true);
  const [teamError, setTeamError] = useState("");
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);

  const load = async () => {
    setTeamLoading(true);
    setTeamError("");
    try {
      const [t, n] = await Promise.all([
        api.getStudentTeam(studentId),
        api.listCollaborationNotes(studentId),
      ]);
      setTeam(t.data);
      setNotes(n.data?.items || []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setTeamError("You don't have access to this student's team space.");
      } else {
        setTeamError(
          err?.response?.data?.detail || "Could not load team space"
        );
      }
    } finally {
      setTeamLoading(false);
      setNotesLoading(false);
    }
  };

  useEffect(() => {
    if (!studentId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  if (teamLoading) {
    return (
      <div className="text-[13px] text-[#6B7280] py-10 text-center">
        Loading team…
      </div>
    );
  }
  if (teamError) {
    return (
      <Card className="border-[#E5E3DB] rounded-2xl" data-testid="team-error">
        <CardContent className="p-6 text-center text-[13px] text-[#7A3A2C]">
          <Warning
            size={16}
            weight="duotone"
            className="inline mr-1 mb-0.5"
          />
          {teamError}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="team-tab">
      <TeamPanel team={team} />
      <NotesComposer
        team={team}
        onPosted={(note) => setNotes((prev) => [...prev, note])}
      />
      <NotesTimeline
        notes={notes}
        notesLoading={notesLoading}
        team={team}
        currentUserId={user?.id}
        isAdmin={!!team?.viewer?.is_admin}
        onChanged={load}
      />
    </div>
  );
}

// ── Team panel ───────────────────────────────────────────────────────

function TeamPanel({ team }) {
  if (!team) return null;
  const members = team.members || [];
  return (
    <Card
      className="border-[#E5E3DB] rounded-2xl"
      data-testid="team-panel"
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h3 className="text-[13.5px] font-semibold text-[#1F2937] heading-font flex items-center gap-1.5">
            <Users
              size={14}
              weight="duotone"
              className="text-[#3F4756]"
            />
            Student team
            <span className="ml-1.5 text-[11px] font-medium text-[#6B7280]">
              · {members.length} provider{members.length === 1 ? "" : "s"}
            </span>
          </h3>
          {team.school?.name && (
            <span className="text-[11px] text-[#6B7280]">
              {team.school.name}
            </span>
          )}
        </div>
        {members.length === 0 ? (
          <p className="text-[12.5px] text-[#6B7280] py-2">
            No active providers assigned yet.
          </p>
        ) : (
          <ul className="divide-y divide-[#F1F2F4] border border-[#F1F2F4] rounded-lg">
            {members.map((m) => (
              <TeamMemberRow key={`${m.kind}-${m.provider_id}`} member={m} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function TeamMemberRow({ member }) {
  // FP3-035B-2 — Refined badge model. Stack up to four semantic
  // badges in a stable order:
  //   1. Primary       — only one across the whole team (server-side guaranteed)
  //   2. Direct Service
  //   3. Evaluation Team
  //   4. Assistant     — assistants are never auto-primary
  // The legacy `kind` ("primary" | "assistant" | "coverage") is
  // still used as a fallback when the refined fields are missing
  // (pre-migration records) so the row never renders empty.
  const badges = [];
  if (member.primary_provider) {
    badges.push({
      label: "Primary",
      testid: `team-badge-primary-${member.provider_id}`,
      className: "bg-[#E8F0EB] text-[#1E3F33]",
    });
  }
  if (member.provides_direct_service) {
    badges.push({
      label: "Direct Service",
      testid: `team-badge-direct-${member.provider_id}`,
      className: "bg-[#E6EFF7] text-[#1F3A5A]",
    });
  }
  if (member.evaluation_team_member) {
    badges.push({
      label: "Evaluation Team",
      testid: `team-badge-eval-${member.provider_id}`,
      className: "bg-[#F1EEF6] text-[#4A3F66]",
    });
  }
  if (member.assistant_assignment) {
    badges.push({
      label: "Assistant",
      testid: `team-badge-assistant-${member.provider_id}`,
      className: "bg-[#F0EDE3] text-[#6B5A2C]",
    });
  }
  if (member.kind === "coverage") {
    badges.push({
      label: "Coverage",
      testid: `team-badge-coverage-${member.provider_id}`,
      className: "bg-[#FAF1DD] text-[#7A5B1F]",
    });
  }
  // Fallback when no refined fields are populated.
  if (badges.length === 0) {
    const legacy = ROLE_BADGE[member.kind] || ROLE_BADGE.primary;
    badges.push({
      label: legacy.label,
      testid: `team-badge-${member.kind}-${member.provider_id}`,
      className: legacy.className,
    });
  }
  const domains = member.assigned_service_domains || [];
  return (
    <li
      className="px-4 py-3 hover:bg-[#FBFAF6]"
      data-testid={`team-member-${member.provider_id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-full bg-[#E8F0EB] text-[#1E3F33] text-[11px] font-semibold flex items-center justify-center shrink-0">
            {(member.first_name?.[0] || "?").toUpperCase()}
            {(member.last_name?.[0] || "").toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-[13px] text-[#1F2937] flex items-center gap-1.5 flex-wrap">
              <span className="font-medium">{member.name}</span>
              {member.role && (
                <span className="text-[11px] text-[#6B7280]">
                  · {member.role}
                </span>
              )}
              {badges.map((b) => (
                <span
                  key={b.label}
                  data-testid={b.testid}
                  className={`text-[10px] uppercase tracking-[0.08em] font-semibold rounded-full px-1.5 py-0.5 ${b.className}`}
                >
                  {b.label}
                </span>
              ))}
              {member.assistant_type && !member.assistant_assignment && (
                <span className="text-[10px] text-[#7A5B1F] bg-[#FAF1DD] rounded-full px-1.5 py-0.5">
                  {member.assistant_type}
                </span>
              )}
              {(member.provider_status === "on_leave" ||
                member.provider_status === "unavailable" ||
                member.provider_status === "paused") && (
                <span className="text-[10px] text-[#7A3A2C] bg-[#F8E8E2] rounded-full px-1.5 py-0.5">
                  {member.provider_status.replace("_", " ")}
                </span>
              )}
            </div>
            <div className="text-[10.5px] text-[#6B7280] mt-0.5">
              {member.assistant_assignment && member.supervising_provider_name && (
                <>
                  Supervised by {member.supervising_provider_name}
                  {member.supervising_provider_role
                    ? ` · ${member.supervising_provider_role}`
                    : ""}
                </>
              )}
              {member.kind === "coverage" && member.coverage && (
                <>
                  <Clock
                    size={9}
                    weight="duotone"
                    className="inline mr-0.5 mb-0.5"
                  />
                  Coverage {member.coverage.starts_on} →{" "}
                  {formatDate(member.coverage.ends_on)}
                  {member.coverage.reason ? ` · ${member.coverage.reason}` : ""}
                </>
              )}
              {!member.assistant_assignment && member.kind !== "coverage"
                && member.schools?.length > 0 && (
                <>
                  {member.schools.map((s) => s.name).join(" · ")}
                </>
              )}
            </div>
            {/* FP3-035B-2 — Assigned service domains chips. Narrow
                surface that tells admins / coverage providers exactly
                what THIS team member is responsible for on THIS
                student. Hidden when empty. */}
            {domains.length > 0 && (
              <div
                className="flex flex-wrap gap-1 mt-1.5"
                data-testid={`team-member-domains-${member.provider_id}`}
              >
                {domains.map((d) => (
                  <span
                    key={d}
                    className="text-[9.5px] uppercase tracking-wider font-medium bg-[#F4F2EC] text-[#5C7088] rounded px-1.5 py-0.5"
                  >
                    {d.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

// ── Composer ─────────────────────────────────────────────────────────

function NotesComposer({ team, onPosted }) {
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general_update");
  const [taggedIds, setTaggedIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [adminOnly, setAdminOnly] = useState(false);

  const studentId = team?.student?.id;
  const isAdmin = !!team?.viewer?.is_admin;
  const teamMembers = team?.members || [];

  const submit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.createCollaborationNote(studentId, {
        message: message.trim(),
        category,
        visibility: adminOnly ? "admin_only" : "student_team",
        tagged_provider_ids: taggedIds,
      });
      toast.success(
        adminOnly ? "Admin-only note posted" : "Team note posted"
      );
      onPosted?.(res.data);
      setMessage("");
      setTaggedIds([]);
      setAdminOnly(false);
      setCategory("general_update");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not post note");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTag = (pid) => {
    setTaggedIds((prev) =>
      prev.includes(pid) ? prev.filter((p) => p !== pid) : [...prev, pid]
    );
  };

  return (
    <Card
      className="border-[#E5E3DB] rounded-2xl"
      data-testid="notes-composer"
    >
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <ChatCircleDots
            size={14}
            weight="duotone"
            className="text-[#3F4756]"
          />
          <h3 className="text-[13.5px] font-semibold text-[#1F2937] heading-font">
            Add team note
          </h3>
          <span className="text-[10.5px] text-[#9AA0A6]">
            · visible to this student's care team
          </span>
        </div>
        <textarea
          data-testid="note-message-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          maxLength={4000}
          placeholder="Share an update, ask a goal question, prep for a meeting…"
          className="w-full text-[12.5px] border border-[#E5E3DB] rounded-md p-2 bg-white focus:outline-none focus:border-[#1E3F33] resize-none"
        />
        <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid="note-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="text-[11.5px] border border-[#E5E3DB] rounded-md px-2 py-1 bg-white"
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
            {teamMembers.length > 1 && (
              <div className="flex items-center gap-1 flex-wrap text-[10.5px]">
                <span className="text-[#6B7280] uppercase tracking-[0.06em]">
                  Tag:
                </span>
                {teamMembers.map((m) => (
                  <button
                    key={m.provider_id}
                    type="button"
                    data-testid={`note-tag-${m.provider_id}`}
                    onClick={() => toggleTag(m.provider_id)}
                    className={`rounded-full px-2 py-0.5 border transition-colors ${
                      taggedIds.includes(m.provider_id)
                        ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                        : "bg-white text-[#3F4756] border-[#E5E3DB] hover:bg-[#FBFAF6]"
                    }`}
                  >
                    {m.first_name}
                  </button>
                ))}
              </div>
            )}
            {isAdmin && (
              <label className="flex items-center gap-1 text-[11px] text-[#6B7280] ml-1">
                <input
                  type="checkbox"
                  data-testid="note-admin-only"
                  checked={adminOnly}
                  onChange={(e) => setAdminOnly(e.target.checked)}
                  className="rounded border-[#E5E3DB]"
                />
                Admin-only
              </label>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            data-testid="note-submit"
            disabled={!message.trim() || submitting}
            onClick={submit}
            className="h-8 text-[12px] bg-[#1E3F33] hover:bg-[#173127] disabled:bg-[#1E3F33]/60 text-white"
          >
            <CaretRight size={12} weight="bold" className="mr-1" />
            {submitting ? "Posting…" : "Post team note"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Notes timeline ───────────────────────────────────────────────────

function NotesTimeline({
  notes,
  notesLoading,
  team,
  currentUserId,
  isAdmin,
  onChanged,
}) {
  // Build provider-name lookup for tag rendering. Computed
  // unconditionally to satisfy rules-of-hooks.
  const providerNameById = useMemo(() => {
    const map = {};
    for (const m of team?.members || []) {
      map[m.provider_id] = m.name;
    }
    return map;
  }, [team]);

  if (notesLoading) {
    return (
      <div className="text-[12.5px] text-[#6B7280] text-center py-4">
        Loading notes…
      </div>
    );
  }
  if (notes.length === 0) {
    return (
      <Card
        className="border-dashed border-[#E5E3DB] rounded-2xl"
        data-testid="notes-empty"
      >
        <CardContent className="p-6 text-center text-[12.5px] text-[#6B7280]">
          No team notes yet. Share the first update for this student's
          care team.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="border-[#E5E3DB] rounded-2xl"
      data-testid="notes-timeline"
    >
      <CardContent className="p-0">
        <ul className="divide-y divide-[#F1F2F4]">
          {notes.map((n) => (
            <NoteRow
              key={n.id}
              note={n}
              providerNameById={providerNameById}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              studentId={team?.student?.id}
              onChanged={onChanged}
            />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function NoteRow({
  note,
  providerNameById,
  currentUserId,
  isAdmin,
  studentId,
  onChanged,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.message);
  const [busy, setBusy] = useState(false);
  // FP3-032D — In-app delete confirmation replaces window.confirm.
  const [pendingDelete, setPendingDelete] = useState(false);

  const canEdit = isAdmin || note.author_user_id === currentUserId;

  const save = async () => {
    if (!draft.trim() || draft.trim() === note.message) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await api.updateCollaborationNote(studentId, note.id, {
        message: draft.trim(),
      });
      toast.success("Note updated");
      setEditing(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const confirmRemove = async () => {
    setBusy(true);
    try {
      await api.deleteCollaborationNote(studentId, note.id);
      toast.success("Note deleted");
      setPendingDelete(false);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    } finally {
      setBusy(false);
    }
  };

  const isAdminOnly = note.visibility === "admin_only";

  return (
    <li
      data-testid={`note-row-${note.id}`}
      className="px-5 py-3"
    >
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="text-[12px] text-[#1F2937]">
          <span className="font-medium">{note.author_name}</span>
          {note.author_role && (
            <span className="text-[#6B7280]"> · {note.author_role}</span>
          )}
          <span className="text-[10.5px] text-[#9AA0A6] ml-1">
            {formatTs(note.created_at)}
            {note.edited_at ? " · edited" : ""}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          {isAdminOnly && (
            <span className="text-[10px] uppercase tracking-[0.08em] font-semibold rounded-full px-1.5 py-0.5 bg-[#F1F2F4] text-[#6B7280]">
              Admin only
            </span>
          )}
          {note.category && note.category !== "general_update" && (
            <span className="text-[10px] uppercase tracking-[0.06em] font-medium rounded-full px-1.5 py-0.5 bg-[#E8F0EB] text-[#1E3F33]">
              {(CATEGORY_OPTIONS.find((c) => c.value === note.category) || {})
                .label || note.category}
            </span>
          )}
          {canEdit && !editing && (
            <>
              <button
                type="button"
                onClick={() => {
                  setDraft(note.message);
                  setEditing(true);
                }}
                data-testid={`note-edit-${note.id}`}
                className="text-[#6B7280] hover:text-[#1E3F33] p-1"
                title="Edit"
              >
                <PencilSimple size={11} />
              </button>
              <button
                type="button"
                onClick={() => setPendingDelete(true)}
                data-testid={`note-delete-${note.id}`}
                disabled={busy}
                className="text-[#6B7280] hover:text-[#7A3A2C] p-1"
                title="Delete"
                aria-label="Delete team note"
              >
                <TrashSimple size={11} />
              </button>
            </>
          )}
        </div>
      </div>
      {editing ? (
        <div className="mt-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            data-testid={`note-edit-input-${note.id}`}
            className="w-full text-[12.5px] border border-[#E5E3DB] rounded-md p-2 bg-white focus:outline-none focus:border-[#1E3F33] resize-none"
          />
          <div className="flex items-center gap-2 mt-2">
            <Button
              size="sm"
              data-testid={`note-edit-save-${note.id}`}
              onClick={save}
              disabled={busy}
              className="h-7 text-[11px] bg-[#1E3F33] hover:bg-[#173127] text-white"
            >
              <Check size={11} className="mr-1" /> Save
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditing(false)}
              className="h-7 text-[11px] border-[#E5E3DB] text-[#6B7280]"
            >
              <X size={11} className="mr-1" /> Cancel
            </Button>
          </div>
        </div>
      ) : (
        <p
          className="text-[12.5px] text-[#3F4756] whitespace-pre-wrap mt-1.5 leading-relaxed"
          data-testid={`note-message-${note.id}`}
        >
          {note.message}
        </p>
      )}
      {note.tagged_provider_ids?.length > 0 && (
        <div className="flex items-center gap-1 mt-1.5 text-[10.5px] text-[#6B7280] flex-wrap">
          <span className="uppercase tracking-[0.06em]">Tagged:</span>
          {note.tagged_provider_ids.map((pid) => (
            <span
              key={pid}
              className="rounded-full px-1.5 py-0.5 bg-[#F1F2F4] text-[#3F4756]"
            >
              @{providerNameById?.[pid] || pid.slice(0, 6)}
            </span>
          ))}
        </div>
      )}
      {/* FP3-032D — In-app delete confirmation. */}
      <ConfirmDialog
        open={pendingDelete}
        onOpenChange={(o) => !busy && setPendingDelete(o)}
        title="Delete team note?"
        description="The note will be removed from the student's collaboration timeline. This cannot be undone."
        confirmLabel="Delete note"
        destructive
        onConfirm={confirmRemove}
        testId={`note-delete-confirm-${note.id}`}
      />
    </li>
  );
}
